<template>
  <v-card class="d-flex flex-column" style="height: 100vh">
    <v-card-title class="primary white--text d-flex align-center">
      <v-icon dark left>mdi-towel</v-icon>
      Duschtuch Aktion Verwaltung
      <v-spacer></v-spacer>
      <v-btn icon dark @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="pa-4 flex-grow-1" style="overflow-y: auto">
      <div v-if="loading" class="text-center pa-10">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <p class="mt-4">Lade Reservierungen...</p>
      </div>
      <div v-else>
        <v-row class="mb-4" align="center">
          <v-col cols="12" md="6">
            <v-text-field
              v-model="search"
              label="Suchen nach Name oder E-Mail..."
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              dense
              hide-details
              clearable></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-chip-group v-model="statusFilter" mandatory active-class="primary--text">
              <v-chip filter value="all">Alle</v-chip>
              <v-chip filter value="reserved">Reserviert</v-chip>
              <v-chip filter value="collected">Abgeholt</v-chip>
              <v-chip filter value="canceled">Storniert</v-chip>
            </v-chip-group>
          </v-col>
        </v-row>

        <v-data-table
          :headers="headers"
          :items="filteredReservations"
          :items-per-page="10"
          class="elevation-1 rounded-lg"
          :no-data-text="'Keine Reservierungen gefunden.'"
          :no-results-text="'Keine Reservierungen für diese Suche gefunden.'">
          <template v-slot:item.createdBy="{ item }">
            <div v-if="item.createdBy">
              <div class="font-weight-bold">{{ item.createdBy.vorname }} {{ item.createdBy.nachname }}</div>
              <div class="text-caption grey--text">{{ item.createdBy.email }}</div>
            </div>
            <div v-else class="grey--text">Unbekannt</div>
          </template>

          <template v-slot:item.created="{ item }">
            {{ formatDate(item.created) }}
          </template>

          <template v-slot:item.content.quantity="{ item }">
            <v-chip small color="primary" class="font-weight-bold">{{ item.content.quantity || 1 }} Stk.</v-chip>
          </template>

          <template v-slot:item.content.status="{ item }">
            <v-chip :color="getStatusColor(item.content.status, item.content)" small label text-color="white">
              {{ getStatusText(item.content.status, item.content) }}
            </v-chip>

            <div v-if="getExpiryInfo(item) && getExpiryInfo(item).days !== null" class="text-caption warning--text mt-1" style="line-height: 1.2">
              <v-icon x-small color="warning">mdi-timer-sand</v-icon>
              Läuft ab in {{ getExpiryInfo(item).days }} Tag(en)
            </div>
          </template>

          <template v-slot:item.actions="{ item }">
            <div v-if="item.content.status === 'reserved'">
              <v-btn small color="success" class="mr-2" @click="updateStatus(item, 'collected')">
                <v-icon left small>mdi-check</v-icon>
                Abgeholt
              </v-btn>
              <v-btn small color="error" outlined @click="showCancelDialog(item)"> Stornieren </v-btn>
            </div>
            <div v-else-if="item.content.status === 'canceled'">
              <v-btn small color="grey" text disabled>Storniert</v-btn>
            </div>
            <div v-else>
              <v-btn small color="grey" text disabled>Abgeschlossen</v-btn>
            </div>
          </template>
        </v-data-table>
      </div>
    </v-card-text>
    <v-dialog v-model="cancelDialog" max-width="400" persistent>
      <v-card v-if="itemToCancel">
        <v-card-title class="text-h5 warning--text">
          <v-icon left color="warning">mdi-alert-circle-outline</v-icon>
          Reservierung stornieren?
        </v-card-title>
        <v-card-text>
          Möchtest du die Reservierung von
          <strong>{{ itemToCancel.createdBy.vorname }} {{ itemToCancel.createdBy.nachname }}</strong>
          wirklich stornieren? Diese Aktion kann nicht rückgängig gemacht werden.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelDialog = false">Abbrechen</v-btn>
          <v-btn color="warning" text @click="confirmCancel">Ja, stornieren</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import api from "@/api"
import { mapGetters } from "vuex"

export default {
  name: "TowelAdmin",
  data() {
    return {
      loading: true,
      reservations: [],
      search: "",
      statusFilter: "all",
      cancelDialog: false,
      itemToCancel: null,
      headers: [
        { text: "Kunde", value: "createdBy", sortable: true },
        { text: "Reserviert am", value: "created", sortable: true },
        { text: "Menge", value: "content.quantity", sortable: false, align: "center" },
        { text: "Status", value: "content.status", sortable: true, align: "center" },
        { text: "Studio", value: "createdBy.studio_id", sortable: true, align: "center" },
        { text: "Aktionen", value: "actions", sortable: false, align: "right" },
      ],
    }
  },
  computed: {
    ...mapGetters("studioStore", ["getStudios"]),

    filteredReservations() {
      let items = this.reservations

      // 1. Filter by status
      if (this.statusFilter !== "all") {
        items = items.filter((r) => r.content.status === this.statusFilter)
      }

      // 2. Filter by search term
      if (this.search && this.search.trim() !== "") {
        const searchTerm = this.search.toLowerCase().trim()
        items = items.filter((item) => {
          const createdBy = item.createdBy
          if (!createdBy) return false

          const fullName = `${createdBy.vorname || ""} ${createdBy.nachname || ""}`.toLowerCase()
          const email = (createdBy.email || "").toLowerCase()

          return fullName.includes(searchTerm) || email.includes(searchTerm)
        })
      }

      return items
    },
  },
  async created() {
    await this.fetchReservations()
  },
  methods: {
    getExpiryInfo(item) {
      if (!item.content || item.content.status !== "reserved") {
        return null
      }
      const warningStartDate = item.content.expiryWarningStartDate
      if (!warningStartDate) {
        return null // Countdown hat nicht begonnen
      }

      const warningStart = new Date(warningStartDate).getTime()
      const now = new Date().getTime()
      const daysSinceWarning = (now - warningStart) / (1000 * 60 * 60 * 24)
      const daysLeft = 5 - daysSinceWarning

      if (daysLeft <= 0) {
        // Countdown ist abgelaufen (falls Benutzer sich noch nicht eingeloggt hat, um es zu triggern)
        // Wir zeigen "0" an, bis der Status sich ändert.
        return { days: 0 }
      }
      return { days: Math.ceil(daysLeft) }
    },
    async fetchReservations() {
      this.loading = true
      try {
        this.reservations = await api.getTowelActionReservations()
      } catch (error) {
        this.$toast.error("Fehler beim Laden der Reservierungen.")
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    async updateStatus(item, newStatus) {
      try {
        const updatedItem = await api.updateTowelReservationStatus(item._id, newStatus)
        const index = this.reservations.findIndex((r) => r._id === item._id)
        if (index !== -1) {
          // Manually update the content object to ensure reactivity
          const newReservations = [...this.reservations]
          newReservations[index] = updatedItem
          this.reservations = newReservations
        }
        this.$toast.success("Status erfolgreich aktualisiert.")
      } catch (error) {
        this.$toast.error("Status konnte nicht aktualisiert werden.")
      }
      this.cancelDialog = false
      this.itemToCancel = null
    },
    showCancelDialog(item) {
      this.itemToCancel = item
      this.cancelDialog = true
    },
    confirmCancel() {
      if (this.itemToCancel) {
        this.updateStatus(this.itemToCancel, "canceled")
      }
    },
    formatDate(timestamp) {
      return this.$moment.unix(timestamp).format("DD.MM.YYYY, HH:mm")
    },
    getStatusColor(status, itemContent = {}) {
      // itemContent als 2. Parameter
      // Prüfe zuerst auf 'abgelaufen'
      if (status === "canceled" && itemContent.expiryReason === "expired") {
        return "grey"
      }
      switch (status) {
        case "reserved":
          return "blue"
        case "collected":
          return "success"
        case "canceled":
          return "error"
        default:
          return "grey"
      }
    },
    getStatusText(status, itemContent = {}) {
      // itemContent als 2. Parameter
      // Prüfe zuerst auf 'abgelaufen'
      if (status === "canceled" && itemContent.expiryReason === "expired") {
        return "Abgelaufen"
      }
      switch (status) {
        case "reserved":
          return "Reserviert"
        case "collected":
          return "Abgeholt"
        case "canceled":
          return "Storniert"
        default:
          return "Unbekannt"
      }
    },
  },
}
</script>
